import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import {
  EPISODE_STATUS_LABELS,
  ProcedureIdSchema,
  type AnesthesiaRecord,
  type Consent,
  type ExpenseSheet,
  type ProcedureId,
} from '@datazo/contracts';
import { Button } from '@/components/ui/button';
import { AuthenticatedImage } from '@/components/ui/authenticated-image';
import { DrawableVitalsChart } from '@/components/DrawableVitalsChart';
import { formatShortDateEs } from '@/shared/lib/format';
import { useAnesthesiaByProcedure } from '@/features/anesthesia/api';
import {
  ANESTHESIA_FORM_VERSION_KEY,
  MED_SEDACION_RE,
  chartVariantForData,
  humanizeKey,
  knownKeysForData,
  medicamentosSedacion,
  sectionsForData,
} from '@/features/anesthesia/form-fields';
import { useConsentByProcedure } from '@/features/consent/api';
import { useExpenseByProcedure } from '@/features/expense/api';
import { usePatientById } from '@/features/patients/api';
import { useUsersList } from '@/features/users/api';
import { useProcedureById } from './api';

/**
 * Vista imprimible de un episodio clínico completo. Layout single-column sin
 * AppShell, con `@media print` que oculta toda la UI excepto el documento.
 *
 * Contenido:
 *   1. Encabezado institucional + identificación del episodio.
 *   2. Resumen del paciente (1 línea bien densa).
 *   3. Datos del procedimiento (fecha, sala, profesionales).
 *   4. Documentos firmados — secciones por documento con sus campos
 *      relevantes + firmas embebidas como imágenes. Cada documento es una
 *      subsección que NO se rompe entre páginas (`break-inside-avoid`).
 *   5. Pie con aviso legal + timestamp.
 *
 * NO incluye la bitácora de auditoría completa — es el documento clínico, no
 * el log forense. La auditoría se exporta por separado desde `/auditoria`.
 */

function isProcedureId(value: string | undefined): value is ProcedureId {
  if (value === undefined) return false;
  return ProcedureIdSchema.safeParse(value).success;
}

export function ProcedurePrintPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const validatedId = isProcedureId(idParam) ? idParam : undefined;
  // `?doc=anestesia` → imprime SOLO el registro de anestesia (no todo el
  // informe clínico). Se usa desde el botón "Descargar / Imprimir" del registro.
  const [searchParams] = useSearchParams();
  const soloAnestesia = searchParams.get('doc') === 'anestesia';

  const procedureQuery = useProcedureById(validatedId);
  const procedure = procedureQuery.data;

  const patientQuery = usePatientById(procedure?.pacienteId);
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  // Cuando es solo-anestesia no pedimos consentimiento ni hoja de gasto.
  const consentQuery = useConsentByProcedure(soloAnestesia ? undefined : validatedId);
  const anesthesiaQuery = useAnesthesiaByProcedure(validatedId);
  const expenseQuery = useExpenseByProcedure(soloAnestesia ? undefined : validatedId);

  useEffect(() => {
    const base = soloAnestesia ? 'Registro de anestesia' : 'Episodio';
    document.title = procedure ? `${base} ${procedure.id} · ${procedure.tipo}` : base;
  }, [procedure, soloAnestesia]);

  const userById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersQuery.data?.items ?? []) {
      map.set(u.id, u.nombre);
    }
    return map;
  }, [usersQuery.data]);

  if (validatedId === undefined) {
    return <ErrorBlock message="Identificador de episodio inválido." />;
  }
  if (procedureQuery.isLoading) {
    return <LoadingBlock />;
  }
  if (procedureQuery.isError || procedure === undefined) {
    return <ErrorBlock message="Episodio no encontrado." />;
  }

  const now = new Date();
  const generatedAt = now.toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' });
  const patient = patientQuery.data;

  return (
    <div className="min-h-screen bg-white text-black print:bg-white">
      {/* Toolbar — solo en pantalla */}
      <div className="no-print bg-surface-alt border-b border-border px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/procedimientos/${procedure.id}`}>
            <ArrowLeft className="w-4 h-4" /> Volver al episodio
          </Link>
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="w-4 h-4" /> Imprimir / Guardar como PDF
        </Button>
      </div>

      <div className="max-w-[820px] mx-auto px-8 py-8 print:px-0 print:py-0">
        {/* Encabezado institucional */}
        <header className="border-b-2 border-slate-900 pb-4 mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/logo-endomedical.png"
              alt="Endomedical"
              className="h-12 w-auto object-contain"
            />
            <div className="text-xs text-slate-600">
              Servicios de endoscopia digestiva
            </div>
          </div>
          <div className="text-right text-xs text-slate-700">
            <div className="font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
              {soloAnestesia ? 'Registro de anestesia' : 'Episodio clínico'}
            </div>
            <div className="font-bold text-base text-slate-900">{procedure.id}</div>
            <div className="mt-1 text-slate-500">Generado: {generatedAt}</div>
          </div>
        </header>

        {/* Datos del paciente — compactos */}
        <section className="mb-6">
          <SectionTitle>Paciente</SectionTitle>
          {patient === undefined ? (
            <div className="text-sm text-slate-500 italic">Cargando datos del paciente…</div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <tbody>
                <KvRow
                  cells={[
                    ['Nombre', `${patient.nombres} ${patient.apellidos}`],
                    ['Documento', `${patient.tipoDoc} ${patient.documento}`],
                  ]}
                />
                <KvRow
                  cells={[
                    ['Fecha nacimiento', patient.fechaNacimiento],
                    ['Sexo', patient.sexo === 'F' ? 'Femenino' : patient.sexo === 'M' ? 'Masculino' : 'Otro'],
                  ]}
                />
                <KvRow
                  cells={[
                    ['EPS', patient.eps ?? '—'],
                    ['Historia clínica', patient.historiaClinica ?? '—'],
                  ]}
                />
                {patient.alergias !== undefined && patient.alergias.trim() !== '' && (
                  <tr>
                    <td className="py-1.5 pr-3 align-top w-[140px] text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      Alergias
                    </td>
                    <td colSpan={3} className="py-1.5 text-sm font-semibold text-red-700">
                      ⚠ {patient.alergias}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </section>

        {/* Datos del procedimiento */}
        <section className="mb-6">
          <SectionTitle>Datos del procedimiento</SectionTitle>
          <table className="w-full text-sm border-collapse">
            <tbody>
              <KvRow
                cells={[
                  ['Tipo', procedure.tipo],
                  ['Estado', EPISODE_STATUS_LABELS[procedure.estado]],
                ]}
              />
              <KvRow
                cells={[
                  ['Fecha', `${formatShortDateEs(procedure.fecha)} · ${procedure.hora}`],
                  ['Sala', procedure.sala ?? '—'],
                ]}
              />
              <KvRow
                cells={[
                  ['Médico tratante', userById.get(procedure.medicoId) ?? procedure.medicoId],
                  [
                    'Anestesiólogo',
                    procedure.anestesiologoId !== undefined
                      ? (userById.get(procedure.anestesiologoId) ?? procedure.anestesiologoId)
                      : '—',
                  ],
                ]}
              />
              <KvRow
                cells={[
                  [
                    'Enfermera',
                    procedure.enfermeraId !== undefined
                      ? (userById.get(procedure.enfermeraId) ?? procedure.enfermeraId)
                      : '—',
                  ],
                  ['Entidad', procedure.entidad ?? '—'],
                ]}
              />
            </tbody>
          </table>
          {procedure.observaciones !== undefined && procedure.observaciones.trim() !== '' && (
            <div className="mt-2 text-xs">
              <span className="font-semibold uppercase tracking-wider text-slate-500">
                Observaciones del procedimiento:
              </span>{' '}
              <span className="text-slate-800">{procedure.observaciones}</span>
            </div>
          )}
          {procedure.estado === 'anulado' &&
            procedure.motivoCancelacion !== undefined &&
            procedure.motivoCancelacion.trim() !== '' && (
              <div className="mt-3 p-2 border border-red-300 bg-red-50 rounded text-xs">
                <div className="text-red-700 font-bold uppercase tracking-wider mb-0.5">
                  Episodio anulado
                </div>
                <div className="text-red-900">{procedure.motivoCancelacion}</div>
                {procedure.canceladoAt !== undefined && (
                  <div className="text-red-700 text-[10px] mt-0.5">
                    Anulado el {new Date(procedure.canceladoAt).toLocaleString('es-CO')}
                  </div>
                )}
              </div>
            )}
        </section>

        {/* Documentos clínicos */}
        <section className="mb-6">
          <SectionTitle>
            {soloAnestesia ? 'Registro de anestesia' : 'Documentos clínicos firmados'}
          </SectionTitle>
          <div className="flex flex-col gap-4">
            {soloAnestesia ? (
              <AnesthesiaSection
                record={anesthesiaQuery.data ?? null}
                loading={anesthesiaQuery.isLoading}
              />
            ) : (
              <>
                <ConsentSection
                  consent={consentQuery.data ?? null}
                  loading={consentQuery.isLoading}
                />
                <AnesthesiaSection
                  record={anesthesiaQuery.data ?? null}
                  loading={anesthesiaQuery.isLoading}
                />
                <ExpenseSection sheet={expenseQuery.data ?? null} loading={expenseQuery.isLoading} />
              </>
            )}
          </div>
        </section>

        {/* Pie */}
        <footer className="mt-10 pt-4 border-t border-slate-300 text-xs text-slate-600 leading-relaxed">
          <p className="mb-2">
            <strong>Aviso legal:</strong> este documento es un resumen del episodio clínico
            generado a partir del expediente digital. Los registros legalmente vinculantes
            corresponden a los PDFs firmados digitalmente y archivados con sus hashes
            criptográficos para cada documento (próxima iteración con firma X.509
            institucional).
          </p>
          <p>
            Documento generado por <strong>Endomedical IPS</strong> el {generatedAt} · Episodio{' '}
            {procedure.id} · Paciente {patient !== undefined ? `${patient.tipoDoc} ${patient.documento}` : '—'}
          </p>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-secciones de documentos                                                */
/* -------------------------------------------------------------------------- */

function ConsentSection({
  consent,
  loading,
}: {
  consent: Consent | null;
  loading: boolean;
}): JSX.Element {
  return (
    <article className="border border-slate-300 rounded p-3 break-inside-avoid">
      <SubsectionHeader title="1. Consentimiento informado" signed={consent !== null} />
      {loading ? (
        <div className="text-xs text-slate-500 italic">Cargando…</div>
      ) : consent === null ? (
        <div className="text-xs text-slate-500 italic">No firmado.</div>
      ) : (
        <div className="text-xs flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            <CheckLine label="Riesgos explicados" ok={consent.riesgosExplicados} />
            <CheckLine label="Alternativas explicadas" ok={consent.alternativasExplicadas} />
            <CheckLine label="Preguntas resueltas" ok={consent.preguntasResueltas} />
          </div>
          {consent.observaciones !== undefined && consent.observaciones.trim() !== '' && (
            <Note label="Observaciones">{consent.observaciones}</Note>
          )}
          <div className="text-[10px] text-slate-500">
            Firmado el {new Date(consent.firmadoEn).toLocaleString('es-CO')}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-1">
            {consent.firmaPaciente !== undefined && (
              <SignatureImg label="Paciente" dataUrl={consent.firmaPaciente} />
            )}
            {consent.firmaAcompanante !== undefined && (
              <SignatureImg
                label={`Acompañante${
                  consent.nombreAcompanante !== undefined ? ` — ${consent.nombreAcompanante}` : ''
                }${
                  consent.parentescoAcompanante !== undefined
                    ? ` (${consent.parentescoAcompanante})`
                    : ''
                }`}
                dataUrl={consent.firmaAcompanante}
              />
            )}
            <SignatureImg
              label={`Médico (${consent.firmaMedicoId})`}
              dataUrl={consent.firmaMedico}
              authUrl={`/api/users/${encodeURIComponent(consent.firmaMedicoId)}/firma`}
            />
          </div>
        </div>
      )}
    </article>
  );
}

/**
 * Reconstruye el Registro de Anestesia tal cual lo llenó el anestesiólogo.
 *
 * El formulario es papel libre: todo va a `freeFormData`. Aquí lo re-agrupamos
 * con las MISMAS secciones/etiquetas del formulario (`ANESTHESIA_SECTIONS`) y
 * mostramos solo los campos que tienen contenido. Cualquier clave que no esté
 * en el catálogo (p. ej. registros viejos con campos ya retirados del form) se
 * imprime igual bajo "Otros datos" — no se pierde nada de lo escrito.
 */
function AnesthesiaFreeForm({ data }: { data: Record<string, unknown> }): JSX.Element | null {
  const valor = (k: string): string | null => {
    const v = data[k];
    return typeof v === 'string' && v.trim() !== '' ? v : null;
  };

  const secciones = sectionsForData(data).map((sec) => ({
    titulo: sec.titulo,
    grupos: sec.grupos
      .map((g) => ({
        titulo: g.titulo,
        campos: g.campos
          .map((c) => ({ label: c.label, value: valor(c.key) }))
          .filter((c): c is { label: string; value: string } => c.value !== null),
      }))
      .filter((g) => g.campos.length > 0),
  })).filter((s) => s.grupos.length > 0);

  const meds = medicamentosSedacion(data);
  const known = knownKeysForData(data);

  // Claves escritas que no están en el catálogo ni son medicamentos de sedación
  // (excluye la marca interna de versión del formato).
  const otros = Object.entries(data)
    .filter(
      ([k, v]) =>
        !known.has(k) &&
        !MED_SEDACION_RE.test(k) &&
        k !== ANESTHESIA_FORM_VERSION_KEY &&
        typeof v === 'string' &&
        v.trim() !== '',
    )
    .map(([k, v]) => ({ label: humanizeKey(k), value: String(v) }));

  if (secciones.length === 0 && meds.length === 0 && otros.length === 0) return null;

  return (
    <div className="flex flex-col gap-2.5">
      {secciones.map((sec) => (
        <div key={sec.titulo}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 border-b border-slate-200 pb-0.5">
            {sec.titulo}
          </div>
          {sec.grupos.map((g, gi) => (
            <div key={gi} className="mb-1.5 last:mb-0">
              {g.titulo !== undefined && (
                <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
                  {g.titulo}
                </div>
              )}
              <div className="grid grid-cols-3 gap-x-3 gap-y-1">
                {g.campos.map((c) => (
                  <div key={c.label} className="break-inside-avoid">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">
                      {c.label}
                    </div>
                    <div className="text-[11px] text-slate-900 whitespace-pre-wrap">{c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {meds.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 border-b border-slate-200 pb-0.5">
            F. Medicamento utilizado en sedación
          </div>
          <ul className="list-disc pl-4 text-[11px] flex flex-col gap-0.5">
            {meds.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {otros.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 border-b border-slate-200 pb-0.5">
            Otros datos
          </div>
          <div className="grid grid-cols-3 gap-x-3 gap-y-1">
            {otros.map((c) => (
              <div key={c.label} className="break-inside-avoid">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">{c.label}</div>
                <div className="text-[11px] text-slate-900 whitespace-pre-wrap">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnesthesiaSection({
  record,
  loading,
}: {
  record: AnesthesiaRecord | null;
  loading: boolean;
}): JSX.Element {
  return (
    <article className="border border-slate-300 rounded p-3 break-inside-avoid">
      <SubsectionHeader title="2. Registro de anestesia" signed={record !== null} />
      {loading ? (
        <div className="text-xs text-slate-500 italic">Cargando…</div>
      ) : record === null ? (
        <div className="text-xs text-slate-500 italic">No firmado.</div>
      ) : (
        <div className="text-xs flex flex-col gap-2">
          {record.preAnestesia !== undefined && (
            <>
              <div className="grid grid-cols-4 gap-2">
                <KvBox
                  label="Peso · Talla"
                  value={`${record.preAnestesia.pesoKg} kg · ${record.preAnestesia.tallaCm} cm`}
                />
                <KvBox
                  label="ASA · Mallampati"
                  value={`ASA ${record.preAnestesia.asa} · M ${record.preAnestesia.mallampati ?? '—'}`}
                />
                <KvBox
                  label="Ayuno · Técnica"
                  value={`${record.preAnestesia.ayunoHoras} h · ${record.tecnica ?? '—'}`}
                />
                <KvBox
                  label="Horario"
                  value={`${record.inicioHora ?? '—'} – ${record.finHora ?? '—'}`}
                />
                <KvBox
                  label="TA inicial"
                  value={`${record.preAnestesia.vitalesIniciales.tensionSistolica}/${record.preAnestesia.vitalesIniciales.tensionDiastolica}`}
                />
                <KvBox
                  label="FC · SatO₂"
                  value={`${record.preAnestesia.vitalesIniciales.frecuenciaCardiaca} lpm · ${record.preAnestesia.vitalesIniciales.saturacionO2}%`}
                />
                {record.preAnestesia.vitalesIniciales.frecuenciaRespiratoria !== undefined && (
                  <KvBox
                    label="FR"
                    value={`${record.preAnestesia.vitalesIniciales.frecuenciaRespiratoria} rpm`}
                  />
                )}
                {record.preAnestesia.vitalesIniciales.temperatura !== undefined && (
                  <KvBox
                    label="T°"
                    value={`${record.preAnestesia.vitalesIniciales.temperatura} °C`}
                  />
                )}
              </div>
              {record.preAnestesia.antecedentes !== undefined &&
                record.preAnestesia.antecedentes.trim() !== '' && (
                  <Note label="Antecedentes">{record.preAnestesia.antecedentes}</Note>
                )}
            </>
          )}
          {record.medicamentos !== undefined && record.medicamentos.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
                Medicamentos administrados ({record.medicamentos.length})
              </div>
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="py-1 pr-2 font-semibold">Código</th>
                    <th className="py-1 pr-2 font-semibold">Nombre</th>
                    <th className="py-1 pr-2 font-semibold">Dosis</th>
                    <th className="py-1 pr-2 font-semibold">Vía</th>
                    <th className="py-1 font-semibold">Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {record.medicamentos.map((m, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-1 pr-2 font-mono">{m.codigo}</td>
                      <td className="py-1 pr-2">{m.nombre}</td>
                      <td className="py-1 pr-2">{m.dosis}</td>
                      <td className="py-1 pr-2">{m.via}</td>
                      <td className="py-1">{m.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Todo lo que escribió el anestesiólogo, tal cual se llenó. */}
          <AnesthesiaFreeForm data={(record.freeFormData ?? {}) as Record<string, unknown>} />
          {record.complicaciones !== undefined && record.complicaciones.trim() !== '' && (
            <Note label="Complicaciones">{record.complicaciones}</Note>
          )}
          {record.observaciones !== undefined && record.observaciones.trim() !== '' && (
            <Note label="Observaciones">{record.observaciones}</Note>
          )}
          {record.graficaVitalesUrl !== undefined && record.graficaVitalesUrl !== '' && (
            <div className="mt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Gráfica de signos vitales
              </div>
              <DrawableVitalsChart
                value={record.graficaVitalesUrl}
                readOnly
                height={420}
                variant={chartVariantForData((record.freeFormData ?? {}) as Record<string, unknown>)}
              />
            </div>
          )}
          <div className="text-[10px] text-slate-500">
            Firmado el{' '}
            {record.firmadoEn ? new Date(record.firmadoEn).toLocaleString('es-CO') : '—'} por{' '}
            {record.anestesiologoId}
          </div>
          <SignatureImg label="Anestesiólogo" dataUrl={record.firmaAnestesiologo ?? undefined} />
        </div>
      )}
    </article>
  );
}

function ExpenseSection({
  sheet,
  loading,
}: {
  sheet: ExpenseSheet | null;
  loading: boolean;
}): JSX.Element {
  return (
    <article className="border border-slate-300 rounded p-3 break-inside-avoid">
      <SubsectionHeader title="3. Hoja de gasto" signed={sheet !== null} />
      {loading ? (
        <div className="text-xs text-slate-500 italic">Cargando…</div>
      ) : sheet === null ? (
        <div className="text-xs text-slate-500 italic">No firmada.</div>
      ) : (
        <div className="text-xs flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            <KvBox label="Líneas" value={`${sheet.lineas.length}`} />
            <KvBox label="Insumos" value={`${sheet.lineas.filter((l) => l.tipo === 'insumo').length}`} />
            <KvBox label="Medicamentos" value={`${sheet.lineas.filter((l) => l.tipo === 'medicamento').length}`} />
          </div>
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-1 pr-2 font-semibold w-[60px]">Tipo</th>
                <th className="py-1 pr-2 font-semibold w-[90px]">Código</th>
                <th className="py-1 pr-2 font-semibold">Nombre</th>
                <th className="py-1 pr-2 font-semibold text-right w-[60px]">Cant.</th>
                <th className="py-1 font-semibold w-[60px]">Unidad</th>
              </tr>
            </thead>
            <tbody>
              {sheet.lineas.map((l, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-b-0">
                  <td className="py-1 pr-2 text-slate-500">
                    {l.tipo === 'insumo' ? 'Insumo' : 'Medic.'}
                  </td>
                  <td className="py-1 pr-2 font-mono">{l.codigo}</td>
                  <td className="py-1 pr-2">{l.nombre}</td>
                  <td className="py-1 pr-2 text-right tabular-nums font-semibold">
                    {l.cantidad}
                  </td>
                  <td className="py-1 text-slate-500">{l.unidad ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sheet.observaciones !== undefined && sheet.observaciones.trim() !== '' && (
            <Note label="Observaciones">{sheet.observaciones}</Note>
          )}
          <div className="text-[10px] text-slate-500">
            Firmada el {sheet.firmadoEn ? new Date(sheet.firmadoEn).toLocaleString('es-CO') : '—'}{' '}
            por {sheet.enfermeraId}
          </div>
          <SignatureImg label="Enfermera" dataUrl={sheet.firmaEnfermera ?? undefined} />
        </div>
      )}
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/* Primitivos                                                                 */
/* -------------------------------------------------------------------------- */

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="text-base font-bold uppercase tracking-wider text-slate-700 border-l-4 border-slate-900 pl-2 mb-3">
      {children}
    </h2>
  );
}

function SubsectionHeader({ title, signed }: { title: string; signed: boolean }): JSX.Element {
  return (
    <header className="flex items-center justify-between gap-2 pb-2 mb-2 border-b border-slate-200">
      <h3 className="font-bold text-sm text-slate-900">{title}</h3>
      <span
        className={
          'inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ' +
          (signed
            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
            : 'bg-slate-100 text-slate-600 border-slate-300')
        }
      >
        {signed ? '✓ Firmado' : '○ No firmado'}
      </span>
    </header>
  );
}

interface KvRowProps {
  cells: ReadonlyArray<[label: string, value: string]>;
}

function KvRow({ cells }: KvRowProps): JSX.Element {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      {cells.map(([label, value], i) => (
        <>
          <td
            key={`k-${i}`}
            className="py-1.5 pr-3 align-top w-[140px] text-xs uppercase tracking-wider text-slate-500 font-semibold"
          >
            {label}
          </td>
          <td key={`v-${i}`} className="py-1.5 pr-6 align-top text-sm">
            {value}
          </td>
        </>
      ))}
    </tr>
  );
}

function KvBox({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="border border-slate-200 rounded p-1.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className="text-xs font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function CheckLine({ label, ok }: { label: string; ok: boolean }): JSX.Element {
  return (
    <div
      className={
        'flex items-center gap-1.5 rounded border px-1.5 py-1 ' +
        (ok
          ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
          : 'bg-slate-50 border-slate-200 text-slate-600')
      }
    >
      <span className="font-bold">{ok ? '✓' : '○'}</span>
      <span>{label}</span>
    </div>
  );
}

function Note({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
        {label}
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-slate-800">
        {children}
      </div>
    </div>
  );
}

/**
 * Firma para la vista imprimible. `dataUrl` es la firma capturada; si viene
 * vacía (el médico usa su firma pre-cargada del perfil) la traemos del endpoint
 * protegido vía `authUrl` — antes quedaba un <img src=""> roto en el impreso.
 */
function SignatureImg({
  label,
  dataUrl,
  authUrl,
}: {
  label: string;
  dataUrl?: string | undefined;
  authUrl?: string | undefined;
}): JSX.Element {
  const sinFirma = <span className="text-[10px] text-slate-400">Sin firma</span>;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
        {label}
      </div>
      <div className="bg-white border border-slate-300 rounded p-1.5 inline-block">
        {dataUrl !== undefined && dataUrl !== '' ? (
          <img src={dataUrl} alt={label} className="max-h-[60px] w-auto" />
        ) : authUrl !== undefined ? (
          <AuthenticatedImage
            url={authUrl}
            alt={label}
            className="max-h-[60px] w-auto"
            fallback={sinFirma}
          />
        ) : (
          sinFirma
        )}
      </div>
    </div>
  );
}

function LoadingBlock(): JSX.Element {
  return (
    <div className="min-h-screen grid place-items-center text-slate-500 text-sm">
      Cargando episodio…
    </div>
  );
}

function ErrorBlock({ message }: { message: string }): JSX.Element {
  return (
    <div className="min-h-screen grid place-items-center text-slate-700 px-6">
      <div className="text-center max-w-md">
        <div className="text-red-700 font-bold mb-2">No fue posible cargar el episodio</div>
        <p className="text-sm text-slate-600 mb-4">{message}</p>
        <Button variant="secondary" asChild>
          <Link to="/procedimientos">
            <ArrowLeft className="w-4 h-4" /> Volver al listado
          </Link>
        </Button>
      </div>
    </div>
  );
}
